
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tag } from 'lucide-react';

const fetchActivePromoCodes = async () => {
  const today = new Date().toISOString().split('T')[0];
  const { data, error } = await supabase
    .from('promo_codes')
    .select('code, description, discount_type, discount_value, max_discount_amount')
    .eq('is_active', true)
    .lte('start_date', today)
    .gte('end_date', today)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
};

const formatDiscount = (type: string, value: number, maxAmt: number | null) => {
  if (type === 'percentage') {
    const suffix = maxAmt ? ` (up to ₹${maxAmt})` : '';
    return `${value}% OFF${suffix}`;
  }
  return `₹${value} OFF`;
};

const AnnouncementBar = () => {
  const location = useLocation();
  const { data: settings, isLoading: settingsLoading, isError } = useSiteSettings();
  const { data: promoCodes = [] } = useQuery({
    queryKey: ['active-promo-codes-announcement'],
    queryFn: fetchActivePromoCodes,
    staleTime: 5 * 60 * 1000,
  });

  const items: { text: string; linkText?: string; linkHref?: string; promoCode?: string }[] = [];

  if (settings?.announcement_text) {
    items.push({
      text: settings.announcement_text,
      linkText: settings.announcement_link_text || undefined,
      linkHref: settings.announcement_link_href || undefined,
    });
  }

  promoCodes.forEach((promo) => {
    const discount = formatDiscount(promo.discount_type, promo.discount_value, promo.max_discount_amount);
    items.push({
      text: promo.description || `Use code for ${discount}`,
      promoCode: promo.code,
    });
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideState, setSlideState] = useState<'visible' | 'sliding-out' | 'sliding-in'>('visible');

  useEffect(() => {
    if (items.length <= 1) return;
    const interval = setInterval(() => {
      setSlideState('sliding-out');
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % items.length);
        setSlideState('sliding-in');
        setTimeout(() => setSlideState('visible'), 50);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, [items.length]);

  if (location.pathname !== '/' && location.pathname !== '/shop') return null;
  if (isError) return null;

  if (settingsLoading) {
    return (
      <div className="bg-foreground text-primary-foreground text-center py-2.5 px-4 text-sm font-medium">
        <Skeleton className="h-5 w-1/2 mx-auto bg-primary/20" />
      </div>
    );
  }

  if (items.length === 0) return null;

  const safeIndex = currentIndex % items.length;
  const current = items[safeIndex];

  const slideClass =
    slideState === 'sliding-out'
      ? 'translate-y-full opacity-0'
      : slideState === 'sliding-in'
        ? '-translate-y-full opacity-0'
        : 'translate-y-0 opacity-100';

  return (
    <div className="bg-foreground text-primary-foreground text-center py-2.5 px-4 text-sm font-medium overflow-hidden">
      <p
        className={`flex items-center justify-center gap-2 transition-all duration-300 ease-in-out ${slideClass}`}
      >
        {current.promoCode && <Tag className="h-3.5 w-3.5 shrink-0" />}
        <span>{current.text}</span>
        {current.promoCode && (
          <Badge variant="secondary" className="text-xs font-bold tracking-wider px-2 py-0.5">
            {current.promoCode}
          </Badge>
        )}
        {current.linkText && current.linkHref && (
          <a href={current.linkHref} className="underline hover:opacity-80 transition-opacity">
            {current.linkText}
          </a>
        )}
      </p>
    </div>
  );
};

export default AnnouncementBar;
